import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ExtensionFrame } from './components/ExtensionFrame';
import AdminLayout from './components/AdminLayout';
import AuthCallback from './components/AuthCallback';
import ExtAuthCallback from './components/ExtAuthCallback';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ExtensionHome from './pages/ExtensionHome';
import PointsPage from './pages/PointsPage';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminActivity from './pages/admin/AdminActivity';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminAnnouncements from './pages/admin/AdminAnnouncements';
import AdminConfig from './pages/admin/AdminConfig';
import AdminProducts from './pages/admin/AdminProducts';
import AdminCodes from './pages/admin/AdminCodes';
import AdminRewards from './pages/admin/AdminRewards';
import AdminRedemptions from './pages/admin/AdminRedemptions';
import AdminQuests from './pages/admin/AdminQuests';
import AdminApprovals from './pages/admin/AdminApprovals';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ShareImpactPage from './pages/ShareImpactPage';
import ShopPage from './pages/ShopPage';
import RewardsPage from './pages/RewardsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import CreatorDashboardPage from './pages/CreatorDashboardPage';
import QuestsPage from './pages/QuestsPage';
import PrivacyPage from './pages/PrivacyPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const adminToken = localStorage.getItem('hp_admin_token');
  const adminUser = JSON.parse(localStorage.getItem('hp_admin_user') || 'null');
  
  if (!adminToken || !adminUser || !adminUser.is_admin) {
    return <Navigate to="/ayo-login" replace />;
  }
  return <AdminLayout>{children}</AdminLayout>;
}

function ExtensionApp() {
  const [searchParams] = useSearchParams();
  const initialPage = searchParams.get('page') || 'home';
  const [page, setPage] = useState(initialPage);

  // Sync with URL query param on mount
  useEffect(() => {
    const p = searchParams.get('page');
    if (p && p !== page) setPage(p);
  }, [searchParams]);

  const renderPage = () => {
    switch (page) {
      case 'home': return <ExtensionHome onNavigate={setPage} />;
      case 'rewards': return <RewardsPage />;
      case 'shop': return <ShopPage />;
      case 'points': return <PointsPage />;
      case 'quests': return <QuestsPage />;
      case 'dashboard': return <DashboardPage />;
      case 'creator': return <CreatorDashboardPage />;
      case 'settings': return <SettingsPage onNavigate={setPage} />;
      case 'share': return <ShareImpactPage onBack={() => setPage('home')} />;
      case 'leaderboard': return <LeaderboardPage onBack={() => setPage('home')} />;
      default: return <ExtensionHome onNavigate={setPage} />;
    }
  };

  return (
    <ExtensionFrame currentPage={page} onNavigate={setPage}>
      {renderPage()}
    </ExtensionFrame>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/app" replace /> : <LandingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/auth-callback" element={<AuthCallback />} />
      <Route path="/ext-auth-callback" element={<ExtAuthCallback />} />
      <Route path="/login" element={user ? <Navigate to="/app" replace /> : <LoginPage />} />
      <Route path="/signup" element={user ? <Navigate to="/app" replace /> : <SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<ProtectedRoute><VerifyEmailPage /></ProtectedRoute>} />
      <Route path="/app" element={<ProtectedRoute><ExtensionApp /></ProtectedRoute>} />
      {/* Hidden admin routes */}
      <Route path="/ayo-login" element={<AdminLoginPage />} />
      <Route path="/ayo-panel" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/ayo-panel/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/ayo-panel/activity" element={<AdminRoute><AdminActivity /></AdminRoute>} />
      <Route path="/ayo-panel/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
      <Route path="/ayo-panel/announcements" element={<AdminRoute><AdminAnnouncements /></AdminRoute>} />
      <Route path="/ayo-panel/config" element={<AdminRoute><AdminConfig /></AdminRoute>} />
      <Route path="/ayo-panel/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
      <Route path="/ayo-panel/codes" element={<AdminRoute><AdminCodes /></AdminRoute>} />
      <Route path="/ayo-panel/rewards" element={<AdminRoute><AdminRewards /></AdminRoute>} />
      <Route path="/ayo-panel/redemptions" element={<AdminRoute><AdminRedemptions /></AdminRoute>} />
      <Route path="/ayo-panel/quests" element={<AdminRoute><AdminQuests /></AdminRoute>} />
      <Route path="/ayo-panel/approvals" element={<AdminRoute><AdminApprovals /></AdminRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
